/**
 * 本人希望欄の希望勤務地・転職希望時期のマスタ（varuna の careerconnection_hope_masters/）。
 * 画面（HopeConditionFields の選択肢・RirekishoRequestsCard の初期値）が共有するので、
 * セッション中は1回だけ取って覚える。住所など判定してほしいテキストが増えたときだけ取り直す。
 * 読み込み中・失敗のあいだは masters が null＝呼び出し側は保存値を触らない
 */
import { computed, reactive } from 'vue'
import { postHopeMasters } from '@/services/careerconnection-client'
import type {
  HopeMasters,
  HopeTurnTime,
  HopeWorkArea
} from '@/utils/format-edit/hopeWorkCondition'

type Status = 'idle' | 'loading' | 'ready' | 'error'

const state = reactive({
  status: 'idle' as Status,
  workAreas: [] as HopeWorkArea[],
  turnTimes: [] as HopeTurnTime[],
  // 後からキーを足すと Vue 2.7 では反応しないので、毎回オブジェクトごと差し替える
  resolved: {} as Record<string, string | null>
})

let inflight: Promise<void> | null = null

async function request(texts: string[]): Promise<void> {
  const hadMasters = state.workAreas.length > 0 && state.turnTimes.length > 0
  if (!hadMasters) state.status = 'loading'
  try {
    const res = await postHopeMasters(texts)
    if (res.workAreas.length && res.turnTimes.length) {
      state.workAreas = res.workAreas
      state.turnTimes = res.turnTimes
    }
    // 失敗応答では判定結果を覚えない（復旧後に問い合わせ直せるように）
    if (res.success) {
      const resolved: Record<string, string | null> = { ...state.resolved }
      texts.forEach((text) => {
        resolved[text] = res.resolvedAreaCodes[text] ?? null
      })
      state.resolved = resolved
    }
    state.status =
      res.success && state.workAreas.length && state.turnTimes.length
        ? 'ready'
        : 'error'
  } catch (e) {
    console.error('[hope-masters] 選択肢の取得に失敗:', e)
    // 取得済みのマスタがあれば選択肢は出し続ける（判定できなかったテキストは次の呼び出しで取り直す）
    state.status = hadMasters ? 'ready' : 'error'
  }
}

/** マスタと、texts それぞれの判定結果が揃うまで取る（揃っていれば何もしない） */
function ensureHopeWorkMasters(texts: string[]): Promise<void> {
  if (inflight) {
    return inflight.then(() => ensureHopeWorkMasters(texts))
  }
  const missing = texts.filter((text) => text && !(text in state.resolved))
  if (state.status === 'ready' && missing.length === 0) {
    return Promise.resolve()
  }
  inflight = request(missing).finally(() => {
    inflight = null
  })
  return inflight
}

export function useHopeWorkMasters() {
  const status = computed(() => state.status)
  const workAreas = computed(() => state.workAreas)
  const turnTimes = computed(() => state.turnTimes)
  const masters = computed<HopeMasters | null>(() =>
    state.status === 'ready'
      ? {
          workAreas: state.workAreas,
          turnTimes: state.turnTimes,
          resolved: state.resolved
        }
      : null
  )
  return {
    status,
    workAreas,
    turnTimes,
    masters,
    ensure: ensureHopeWorkMasters
  }
}

/** テスト用: 状態を初期化 */
export function __resetHopeWorkMastersForTest(): void {
  state.status = 'idle'
  state.workAreas = []
  state.turnTimes = []
  state.resolved = {}
  inflight = null
}
